import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {
  FinanceProjectOption,
  FinanceService,
  RateCardPeriodPage,
  RateCardPeriodQuery,
  RateCardPeriodRow,
  ReferenceEntry,
} from '../../finance.service';

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * Resource Rate Card tab inside Finance Details. View-only first cut:
 * lists rate-card-period rows joined to resource / employee / project /
 * active assignment. Endpoint is finance-admin gated; the host page also
 * hides the tab button for non-admins.
 *
 * Standalone component — pulls in its own Material imports so the parent
 * FinanceModule doesn't need to learn about it.
 */
@Component({
  selector: 'app-finance-rate-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './finance-rate-card.component.html',
  styleUrl: './finance-rate-card.component.scss',
})
export class FinanceRateCardComponent implements OnInit, OnDestroy {
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  /**
   * Figma column order — Resource (sticky), History, Edit, D&T India Leader,
   * Start Date, End Date, Tier, Base Rate, Office Exp, D&T Cost, Project Cost,
   * PO Type, Employer. History + Edit icons route to existing pages; no
   * Rate-Card-specific edit UI exists yet so for v1 they reuse the assignment
   * Edit + Project History destinations.
   */
  readonly displayedColumns: string[] = [
    'resource',
    'history',
    'edit',
    'dntIndiaLeader',
    'startDate',
    'endDate',
    'tierModel',
    'baseRate',
    'officeExpRecovery',
    'dntCost',
    'projectCost',
    'poType',
    'employer',
  ];

  rows: RateCardPeriodRow[] = [];
  totalElements = 0;
  totalPages = 1;
  page = 0;
  size = DEFAULT_PAGE_SIZE;

  searchText = '';
  selectedProjectIds: number[] = [];
  selectedSupportingLobs: string[] = [];

  /** Reference data for the inline filters. */
  projectOptions: FinanceProjectOption[] = [];
  supportingLobOptions: ReferenceEntry[] = [];

  /** In-panel search text for the Projects dropdown — same searchable
   *  pattern as Resource Info's Projects filter. Empty string ⇒ show all. */
  projectFilterText = '';

  get filteredProjectOptions(): FinanceProjectOption[] {
    const q = this.projectFilterText.trim().toLowerCase();
    if (!q) return this.projectOptions;
    return this.projectOptions.filter((p) =>
      (p.projectName ?? '').toLowerCase().includes(q)
      || (p.projectIdCode ?? '').toLowerCase().includes(q)
      || String(p.id).includes(q),
    );
  }

  onProjectsPanelOpenedChange(opened: boolean): void {
    if (!opened) this.projectFilterText = '';
  }

  loading = false;
  errorMessage: string | null = null;

  private readonly searchInput$ = new Subject<void>();
  private readonly subs = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly financeService: FinanceService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          this.page = 0;
          this.load();
        }),
    );
    this.financeService.listReferenceEntries('supporting-lob').subscribe({
      next: (opts) => (this.supportingLobOptions = opts),
      error: () => (this.supportingLobOptions = []),
    });
    // Project Catalog endpoint is live now — same source as Resource Info.
    this.financeService.listFinanceProjects().subscribe({
      next: (opts) => (this.projectOptions = opts),
      error: () => (this.projectOptions = []),
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSearchChange(): void {
    this.searchInput$.next();
  }

  clearSearch(): void {
    this.searchText = '';
    this.page = 0;
    this.load();
  }

  onProjectsFilterChange(): void {
    this.page = 0;
    this.load();
  }

  onSupportingLobsFilterChange(): void {
    this.page = 0;
    this.load();
  }

  onPageEvent(e: PageEvent): void {
    this.page = e.pageIndex;
    this.size = e.pageSize;
    this.load();
  }

  /**
   * Route to the full-page Project History view for this row's resource.
   * The Rate Card tab in History will land later; for now we reuse the
   * existing assignment history page since same resource → same audit feed.
   */
  navigateToHistory(row: RateCardPeriodRow): void {
    if (!row.resourceId) return;
    this.router.navigate(['/finance/details/history', row.resourceId], {
      queryParams: { name: row.resourceName },
    });
  }

  /**
   * Route to the Edit Project view. v2 will deep-link into a Rate Card
   * sub-tab on Edit Project; v1 just lands on the assignment edit form.
   */
  navigateToEdit(row: RateCardPeriodRow): void {
    if (!row.resourceId) return;
    this.router.navigate(['/finance/details/edit', row.resourceId], {
      queryParams: { name: row.resourceName },
    });
  }

  trackByRateCardId = (_: number, row: RateCardPeriodRow): number | string =>
    row.rateCardId ?? `${row.resourceId}:${row.startDate}`;

  private buildQuery(): RateCardPeriodQuery {
    return {
      search: this.searchText.trim() || undefined,
      projectIds: this.selectedProjectIds.length ? this.selectedProjectIds : undefined,
      supportingLobs: this.selectedSupportingLobs.length ? this.selectedSupportingLobs : undefined,
      page: this.page,
      size: this.size,
    };
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.financeService.listRateCardPeriods(this.buildQuery()).subscribe({
      next: (data: RateCardPeriodPage) => {
        this.totalElements = data.totalElements;
        this.totalPages = data.totalPages;
        this.page = data.page;
        this.size = data.size;
        this.rows = this.markContinuations(data.rows ?? []);
        this.loading = false;
      },
      error: (err) => {
        // 403 lands here when a non-admin somehow reaches the endpoint
        // (shouldn't happen — the tab button is hidden — but keep a
        // friendly message instead of a crash).
        this.rows = [];
        this.totalElements = 0;
        this.totalPages = 1;
        this.errorMessage = err?.status === 403
          ? 'You do not have permission to view rate card data.'
          : 'Failed to load rate card data. Please try again.';
        this.loading = false;
      },
    });
  }

  /**
   * Same continuation-row trick Resource Info uses: when row N has the
   * same employee as row N-1, blank out the Resource cell so adjacent
   * rows for the same person read as one group. Default BE sort already
   * groups same-employee rows together.
   */
  private markContinuations(rows: RateCardPeriodRow[]): RateCardPeriodRow[] {
    let prevEmployeeId: string | undefined;
    return rows.map((r) => {
      const isContinuation = !!prevEmployeeId && r.employeeId === prevEmployeeId;
      prevEmployeeId = r.employeeId;
      return { ...r, isContinuation };
    });
  }
}
